import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	isLoggedIn,
	getUser,
	logout,
	verifySession,
	getCurrentSubscription,
	cancelStripeSubscription,
	getUserInfo,
	requestPasswordReset,
	verifyResetCode,
	changePassword,
	resendConfirmationEmail,
	getSubscriptionTypes,
} from "../services/authService";
import SubscriptionPlans from "../components/SubscriptionPlans";
import StripePaymentModal from "../components/StripePaymentModal";

function Dashboard() {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("profile");

	// Subscription state
	const [subscription, setSubscription] = useState(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);
	const [apiPlans, setApiPlans] = useState(null);

	// Payment modal state
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState(null);

	// Cancel subscription state
	const [cancelLoading, setCancelLoading] = useState(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);

	// Message state
	const [message, setMessage] = useState({ type: "", text: "" });

	// Resend confirmation state
	const [resendingEmail, setResendingEmail] = useState(false);
	const [resendMessage, setResendMessage] = useState("");

	// Password change state
	const [pwStep, setPwStep] = useState(0); // 0=idle, 1=code sent, 2=enter new password
	const [pwCode, setPwCode] = useState("");
	const [pwNew, setPwNew] = useState("");
	const [pwConfirm, setPwConfirm] = useState("");
	const [pwLoading, setPwLoading] = useState(false);
	const [pwMessage, setPwMessage] = useState({ type: "", text: "" });

	useEffect(() => {
		const checkSession = async () => {
			if (!isLoggedIn()) {
				navigate("/login");
				return;
			}

			const isValid = await verifySession();
			if (!isValid) {
				navigate("/login");
				return;
			}

			setUser(getUser());
			setLoading(false);
		};

		checkSession();
	}, [navigate]);

	// Load subscription when subscription tab is active
	useEffect(() => {
		if (activeTab === "subscription" && !subscription && !subscriptionLoading) {
			loadSubscription();
		}
	}, [activeTab]);

	const loadSubscription = async () => {
		setSubscriptionLoading(true);
		try {
			const [subResult, typesResult] = await Promise.all([
				getCurrentSubscription(),
				getSubscriptionTypes(),
			]);
			if (subResult.success) {
				setSubscription(subResult.subscription);
			}
			if (typesResult.success) {
				setApiPlans(typesResult.types);
			}
		} catch (error) {
			console.error("Error loading subscription:", error);
		} finally {
			setSubscriptionLoading(false);
		}
	};

	const handleLogout = () => {
		logout();
		navigate("/login");
	};

	const handleSelectPlan = (plan) => {
		setSelectedPlan(plan);
		setShowPaymentModal(true);
	};

	const handlePaymentSuccess = async () => {
		setMessage({ type: "success", text: "Paiement réussi ! Votre abonnement est maintenant actif." });
		setShowPaymentModal(false);
		setSelectedPlan(null);

		// Refresh user data and subscription
		const userInfo = await getUserInfo();
		if (userInfo) {
			setUser(userInfo);
		}
		await loadSubscription();

		// Clear message after 5 seconds
		setTimeout(() => setMessage({ type: "", text: "" }), 5000);
	};

	const handleCancelSubscription = async () => {
		setCancelLoading(true);
		try {
			const result = await cancelStripeSubscription();
			if (result.success) {
				setMessage({ type: "success", text: "Votre abonnement a été annulé." });
				setShowCancelConfirm(false);
				await loadSubscription();

				// Refresh user data
				const userInfo = await getUserInfo();
				if (userInfo) {
					setUser(userInfo);
				}
			} else {
				setMessage({ type: "error", text: result.error || "Erreur lors de l'annulation" });
			}
		} catch (error) {
			setMessage({ type: "error", text: "Erreur lors de l'annulation" });
		} finally {
			setCancelLoading(false);
			setTimeout(() => setMessage({ type: "", text: "" }), 5000);
		}
	};

	const handleRequestPwReset = async () => {
		setPwLoading(true);
		setPwMessage({ type: "", text: "" });
		const result = await requestPasswordReset(user.email);
		setPwLoading(false);
		if (result.success) {
			setPwStep(1);
			setPwMessage({ type: "success", text: "Un code a été envoyé à votre adresse email." });
		} else {
			setPwMessage({ type: "error", text: result.error });
		}
	};

	const handleVerifyCode = async () => {
		setPwLoading(true);
		setPwMessage({ type: "", text: "" });
		const result = await verifyResetCode(user.email, pwCode);
		setPwLoading(false);
		if (result.success) {
			setPwStep(2);
			setPwMessage({ type: "", text: "" });
		} else {
			setPwMessage({ type: "error", text: result.error });
		}
	};

	const handleChangePassword = async () => {
		if (pwNew !== pwConfirm) {
			setPwMessage({ type: "error", text: "Les mots de passe ne correspondent pas." });
			return;
		}
		if (pwNew.length < 6) {
			setPwMessage({ type: "error", text: "Le mot de passe doit contenir au moins 6 caractères." });
			return;
		}
		setPwLoading(true);
		setPwMessage({ type: "", text: "" });
		const result = await changePassword(user.email, pwCode, pwNew);
		setPwLoading(false);
		if (result.success) {
			setPwMessage({ type: "success", text: "Mot de passe modifié avec succès !" });
			setPwStep(0);
			setPwCode("");
			setPwNew("");
			setPwConfirm("");
			setTimeout(() => setPwMessage({ type: "", text: "" }), 5000);
		} else {
			setPwMessage({ type: "error", text: result.error });
		}
	};

	const getCurrentPlanId = () => {
		if (!subscription) return 1; // Default to free plan
		return subscription.idAbonnementType || 1;
	};

	if (loading) {
		return (
			<div className="page">
				<div className="dashboard-container">
					<div className="dashboard-card">
						<p className="dashboard-loading">Chargement...</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="page">
			<div className="dashboard-container">
				<div className="dashboard-card dashboard-card-wide">
					<h1 className="dashboard-title">Mon compte</h1>

					{/* Message */}
					{message.text && (
						<div className={`dashboard-message dashboard-message-${message.type}`}>
							{message.text}
						</div>
					)}

					{/* Tabs */}
					<div className="dashboard-tabs">
						<button
							className={`dashboard-tab ${activeTab === "profile" ? "active" : ""}`}
							onClick={() => setActiveTab("profile")}
						>
							Profil
						</button>
						<button
							className={`dashboard-tab ${activeTab === "subscription" ? "active" : ""}`}
							onClick={() => setActiveTab("subscription")}
						>
							Abonnement
						</button>
					</div>

					{/* Profile Tab */}
					{activeTab === "profile" && (
						<div className="dashboard-tab-content">
							<div className="dashboard-welcome">
								<p>
									Bienvenue, <strong>{user?.prenom} {user?.nom}</strong>
								</p>
								<p className="dashboard-email">{user?.email}</p>
							</div>

							{user && !user.emailVerifie && (
								<div className="dashboard-warning">
									Votre email n'est pas encore vérifié. Veuillez vérifier votre boîte mail.
									<button
										className="resend-email-button"
										onClick={async () => {
											setResendingEmail(true);
											setResendMessage("");
											const result = await resendConfirmationEmail(user.email);
											setResendingEmail(false);
											if (result.success) {
												setResendMessage("Email de confirmation renvoyé !");
											} else {
												setResendMessage(result.error || "Erreur lors de l'envoi");
											}
										}}
										disabled={resendingEmail}
									>
										{resendingEmail ? "Envoi..." : "Renvoyer l'email de confirmation"}
									</button>
									{resendMessage && <p className="resend-message">{resendMessage}</p>}
								</div>
							)}

							<div className="dashboard-info">
								<h3>Votre abonnement actuel</h3>
								{user?.abonnement ? (
									<p className="dashboard-subscription">
										{user.abonnement}
									</p>
								) : (
									<p className="dashboard-no-subscription">
										Plan Gratuit
									</p>
								)}
							</div>

							{/* Password change section */}
							<div className="dashboard-info">
								<h3>Modifier le mot de passe</h3>
								{pwMessage.text && (
									<div className={`dashboard-message dashboard-message-${pwMessage.type}`}>
										{pwMessage.text}
									</div>
								)}
								{pwStep === 0 && (
									<button
										onClick={handleRequestPwReset}
										className="dashboard-password-button"
										disabled={pwLoading}
									>
										{pwLoading ? "Envoi..." : "Envoyer un code de vérification"}
									</button>
								)}
								{pwStep === 1 && (
									<div className="dashboard-password-form">
										<input
											type="text"
											placeholder="Code à 6 chiffres"
											value={pwCode}
											onChange={(e) => setPwCode(e.target.value)}
											className="dashboard-input"
											maxLength={6}
										/>
										<button
											onClick={handleVerifyCode}
											className="dashboard-password-button"
											disabled={pwLoading || !pwCode}
										>
											{pwLoading ? "Vérification..." : "Vérifier le code"}
										</button>
										<button
											onClick={() => { setPwStep(0); setPwCode(""); setPwMessage({ type: "", text: "" }); }}
											className="dashboard-password-cancel"
										>
											Annuler
										</button>
									</div>
								)}
								{pwStep === 2 && (
									<div className="dashboard-password-form">
										<input
											type="password"
											placeholder="Nouveau mot de passe"
											value={pwNew}
											onChange={(e) => setPwNew(e.target.value)}
											className="dashboard-input"
										/>
										<input
											type="password"
											placeholder="Confirmer le mot de passe"
											value={pwConfirm}
											onChange={(e) => setPwConfirm(e.target.value)}
											className="dashboard-input"
										/>
										<button
											onClick={handleChangePassword}
											className="dashboard-password-button"
											disabled={pwLoading || !pwNew || !pwConfirm}
										>
											{pwLoading ? "Modification..." : "Changer le mot de passe"}
										</button>
										<button
											onClick={() => { setPwStep(0); setPwCode(""); setPwNew(""); setPwConfirm(""); setPwMessage({ type: "", text: "" }); }}
											className="dashboard-password-cancel"
										>
											Annuler
										</button>
									</div>
								)}
							</div>

							<button onClick={handleLogout} className="dashboard-logout-button">
								Se déconnecter
							</button>
						</div>
					)}

					{/* Subscription Tab */}
					{activeTab === "subscription" && (
						<div className="dashboard-tab-content">
							{subscriptionLoading ? (
								<p className="dashboard-loading">Chargement des abonnements...</p>
							) : (
								<>
									{/* Current subscription info */}
									{subscription && subscription.idAbonnementType > 1 && (
										<div className="dashboard-current-subscription">
											<h3>Votre abonnement actif</h3>
											<div className="subscription-details">
												<p><strong>Plan :</strong> {subscription.libelle}</p>
												<p><strong>Prix :</strong> {subscription.prix}€/mois</p>
												{subscription.dateProchainPaiement && (
													<p><strong>Prochain paiement :</strong> {new Date(subscription.dateProchainPaiement).toLocaleDateString()}</p>
												)}
											</div>

											<button
												className="dashboard-cancel-button"
												onClick={() => setShowCancelConfirm(true)}
												disabled={cancelLoading}
											>
												Annuler l'abonnement
											</button>
										</div>
									)}

									{/* Subscription plans */}
									<SubscriptionPlans
										currentPlanId={getCurrentPlanId()}
										onSelectPlan={handleSelectPlan}
										loading={subscriptionLoading}
										apiPlans={apiPlans}
									/>
								</>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Payment Modal */}
			<StripePaymentModal
				isOpen={showPaymentModal}
				onClose={() => {
					setShowPaymentModal(false);
					setSelectedPlan(null);
				}}
				selectedPlan={selectedPlan}
				onPaymentSuccess={handlePaymentSuccess}
			/>

			{/* Cancel Confirmation Modal */}
			{showCancelConfirm && (
				<div className="modal-overlay" onClick={() => setShowCancelConfirm(false)}>
					<div className="modal-container modal-small" onClick={(e) => e.stopPropagation()}>
						<h3>Confirmer l'annulation</h3>
						<p>Êtes-vous sûr de vouloir annuler votre abonnement ?</p>
						<p className="modal-warning">
							Votre abonnement restera actif jusqu'à la fin de la période de facturation en cours.
						</p>
						<div className="modal-buttons">
							<button
								className="modal-button modal-button-cancel"
								onClick={() => setShowCancelConfirm(false)}
								disabled={cancelLoading}
							>
								Retour
							</button>
							<button
								className="modal-button modal-button-danger"
								onClick={handleCancelSubscription}
								disabled={cancelLoading}
							>
								{cancelLoading ? "Annulation..." : "Confirmer l'annulation"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default Dashboard;
